/**
 * Shared formatters for task schedule display, in the app language.
 * Used by the task detail panel and an empty chart's next fill.
 */
import type { TaskSchedule } from "@/api/tasks";
import { formatDate, translate } from "@/i18n";
import { libScheduleMessages } from "@/i18n/messages/lib-schedule";

/** The short weekday name of `rrule_day` (0 = Monday). */
function weekdayName(day: number | null | undefined): string {
  // 2024-01-01 was a Monday.
  return formatDate(new Date(Date.UTC(2024, 0, 1 + (day ?? 0))), {
    weekday: "short",
    timeZone: "UTC",
  });
}

export function formatNextRun(iso: string | null, tz: string): string {
  if (!iso) return "—";
  try {
    return formatDate(iso, {
      timeZone: tz !== "UTC" ? tz : undefined,
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

/** A run's short weekday and time, as "Mon 09:00", in the schedule's timezone. */
export function formatRunDay(iso: string, tz: string): string {
  return formatDate(iso, {
    timeZone: tz !== "UTC" ? tz : undefined,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function scheduleLabel(s: TaskSchedule): string {
  if (s.schedule_type === "once") {
    return translate(libScheduleMessages, "once", {
      when: formatNextRun(s.scheduled_at, s.timezone),
    });
  }
  const time = s.rrule_time?.slice(0, 5) ?? "09:00";
  if (s.rrule === "daily" || s.rrule === "weekdays")
    return translate(libScheduleMessages, s.rrule, { time });
  if (s.rrule === "weekly" || s.rrule === "biweekly")
    return translate(libScheduleMessages, s.rrule, {
      day: weekdayName(s.rrule_day),
      time,
    });
  if (s.rrule === "monthly")
    return translate(libScheduleMessages, "monthly", {
      day: s.rrule_day ?? 1,
      time,
    });
  return s.rrule || translate(libScheduleMessages, "scheduled");
}
